import { ApiError } from "@carepass/api-client";
import { useState } from "react";
import { useParams } from "react-router";
import { useAdminNavigation } from "../../app/navigation/useAdminNavigation";
import { canAccessAdminPage } from "../../app/permissions";
import { useAdminSession } from "../../app/providers";
import { parseAdminEntityId, pathForAdminEntityScreen, pathForReconciliationIssue } from "../../app/routes";
import type { Schemas } from "../workspace/api.generated";
import { StateNotice } from "../workspace/shared";
import { ReconciliationIssueDetailView } from "./components/ReconciliationIssueDetailView";
import { useReconciliationIssue, useReconciliationMutation } from "./queries";

function queryState(query: ReturnType<typeof useReconciliationIssue>) {
  if (query.isPending) return "loading" as const;
  if (query.error instanceof ApiError && query.error.kind === "forbidden") return "denied" as const;
  if (query.error instanceof ApiError && query.error.kind === "not-found") return "not-found" as const;
  if (query.isError) return "error" as const;
  return "ready" as const;
}

interface RelatedTarget {
  readonly href?: string;
  readonly label?: string;
  readonly denied?: boolean;
}

function relatedTarget(
  issue: Schemas["ReconciliationIssueView"] | undefined,
  authorities: readonly string[],
): RelatedTarget | undefined {
  if (!issue?.businessRefType || !issue.businessRefId || !/^\d+$/.test(issue.businessRefId)) return undefined;
  const id = Number(issue.businessRefId);
  if (!Number.isSafeInteger(id) || id <= 0) return undefined;
  const type = issue.businessRefType.toUpperCase();
  const target = type === "TRANSFER"
    ? { pageId: "ADM-S10" as const, href: pathForAdminEntityScreen("10", id), label: `송금 ${id} 열기` }
    : type === "TRANSACTION"
      ? { pageId: "ADM-S08" as const, href: pathForAdminEntityScreen("08", id), label: `거래 ${id} 열기` }
      : type === "MEMBER"
        ? { pageId: "ADM-S06" as const, href: pathForAdminEntityScreen("06", id), label: `회원 ${id} 열기` }
        : type === "REFUND"
          ? { pageId: "ADM-S23" as const, href: pathForAdminEntityScreen("23", id), label: `환불 ${id} 열기` }
          : type === "BENEFIT_CLAIM"
            ? { pageId: "ADM-S24" as const, href: pathForAdminEntityScreen("24", id), label: `혜택지원 ${id} 열기` }
            : type === "INQUIRY"
              ? { pageId: "ADM-S25" as const, href: pathForAdminEntityScreen("25", id), label: `문의 ${id} 열기` }
              : type === "PAYMENT"
                ? {
                  pageId: "ADM-S19" as const,
                  href: `/operations/logs?tab=external&businessRefType=PAYMENT&businessRefId=${encodeURIComponent(issue.businessRefId)}`,
                  label: "관련 API 로그 보기",
                }
                : undefined;
  if (!target) return undefined;
  return canAccessAdminPage(target.pageId, authorities, "operations")
    ? { href: target.href, label: target.label }
    : { denied: true };
}

function ReconciliationIssueDetailPageForIssue({ runId, issueId }: { readonly runId: number; readonly issueId: number }) {
  const session = useAdminSession();
  const query = useReconciliationIssue(issueId);
  const mutation = useReconciliationMutation();
  const navigation = useAdminNavigation({
    contextKey: `reconciliation-issue:${issueId}`,
    defaultListPath: "/operations/reconciliations?tab=issues",
    originLabel: "대사 이슈 상세",
  });
  const [notice, setNotice] = useState("");
  const authorities = session.authSession?.authorities ?? [];
  const actualRunId = query.data?.reconciliationRunId;
  const actualHref = actualRunId ? pathForReconciliationIssue(actualRunId, issueId) : undefined;
  const related = relatedTarget(query.data, authorities);
  const relatedHref = related?.href;

  const resolve = async (memo: string) => {
    const result = await mutation.mutateAsync({ kind: "resolve", issueId, body: { memo } }) as Schemas["ReconciliationIssueView"];
    setNotice(`이슈 ${result.id}가 ${result.status} 상태로 갱신되었습니다.`);
    const refreshed = await query.refetch();
    if (refreshed.error) throw refreshed.error;
    return result;
  };

  return <ReconciliationIssueDetailView
    urlRunId={runId}
    issueId={issueId}
    issue={query.data}
    state={queryState(query)}
    canManage={session.authSession?.authorities?.includes("PROVIDER_MANAGE") ?? false}
    backHref={navigation.backPath}
    backLabel={navigation.backLabel}
    onBack={navigation.backToList}
    actualRunHref={actualHref}
    onOpenActualRun={actualHref ? () => navigation.openRelated(actualHref, "대사 이슈 상세") : undefined}
    relatedHref={relatedHref}
    relatedLabel={related?.label}
    relatedDenied={related?.denied}
    onOpenRelated={relatedHref ? () => navigation.openRelated(relatedHref, "대사 이슈 상세") : undefined}
    notice={notice}
    onRetry={() => { void query.refetch(); }}
    onResolve={resolve}
  />;
}

export function ReconciliationIssueDetailPage() {
  const params = useParams();
  const runId = parseAdminEntityId(params.runId);
  const issueId = parseAdminEntityId(params.issueId);
  if (runId === null || issueId === null) return <StateNotice state="not-found" />;
  return <ReconciliationIssueDetailPageForIssue key={`${runId}:${issueId}`} runId={runId} issueId={issueId} />;
}
